import React, { useState } from 'react'
import {
  Box,
  Button,
  Container,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { LocalGridFragment, LocalGridType } from '../../generated/graphql'
import { LocalGridBasicInfoTab } from './basic-info-tab/LocalGridBasicInfoTab'
import { useLocalGrids } from '../../local-grid/use-local-grids'
import { useAlert } from '../alert/AlertProvider'
import {
  activeVersionVar,
  calculatorVar,
  projectVar,
} from '../../apollo/reactive-vars'
import { LocalGridEnergySystemTab } from './energy-system-tab/LocalGridEnergySystemTab'
import { LocalGridReportTab } from './report-tab/LocalGridReportTab'
import { useAuthorization } from '../../authorization/use-authorization'
import { useLocalGridUtil } from '../../local-grid/use-local-grid-util'
import { LocalGridBoreholeFieldTab } from './borehole-field-tab/LocalGridBoreholeFieldTab'
import { isConfirmationValid } from '../../borehole-field-container/borehole-field-confirmation-validator'
import { LabelWithAlertBadge } from '../common/LabelWithAlertBadge'
import {
  createHarvestedDataForSystemContainer,
  createWasteInputDataForSystemContainer,
  createConfirmationData,
} from '../../borehole-field-container/borehole-field-data-creator'

type Props = {
  localGrid: LocalGridFragment
  onClose: () => void
}

export function LocalGridDrawerContent({
  localGrid,
  onClose,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { alert } = useAlert()
  const { deleteLocalGrid } = useLocalGrids()
  const activeVersion = useReactiveVar(activeVersionVar)
  const project = useReactiveVar(projectVar)
  const [tab, setTab] = useState(0)
  const canEditProject = useAuthorization().canEditProject(project)
  const calculator = useReactiveVar(calculatorVar)
  const { waterGrid, connectedWaterGridProvider } = useLocalGridUtil(localGrid)

  if (!activeVersion) {
    return <div />
  }

  const localGridCalculator = calculator?.findLocalGrid(localGrid.id)

  const providers = localGridCalculator?.getProviders() ?? []

  const harvested = createHarvestedDataForSystemContainer(providers)
  const wasteInput = createWasteInputDataForSystemContainer(providers)
  const currentBoreholeData = createConfirmationData(
    localGrid,
    harvested,
    wasteInput,
  )

  const onDelete = () => {
    alert(
      t(`localGrid.${localGrid.type}.deleteDialog.title`),
      t(`localGrid.${localGrid.type}.deleteDialog.message`),
      [
        { title: t('common.cancel') },
        {
          title: t(`localGrid.${localGrid.type}.deleteDialog.confirm`),
          color: 'secondary',
          onPress: async () => {
            await deleteLocalGrid(
              activeVersion,
              localGrid,
              waterGrid,
              connectedWaterGridProvider?.id,
            )
            onClose()
          },
        },
      ],
    )
  }

  return (
    <Box>
      <Container>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={4}
          mb={2}
        >
          <Typography variant="h3">
            {t(`localGridType.${localGrid.type}`)}
          </Typography>

          <Button
            variant="contained"
            onClick={onDelete}
            disabled={!canEditProject}
          >
            {t(`localGrid.${localGrid.type}.delete`)}
          </Button>
        </Box>

        <Tabs value={tab} onChange={(_, index: number) => setTab(index)}>
          <Tab
            id="basic-info-tab"
            label={t(`localGrid.${localGrid.type}.tabs.basicInfo.title`)}
          />
          <Tab
            id="energy-system-tab"
            label={t('localGrid.tabs.energySystem.title')}
          />
          <Tab id="report-tab" label={t('report.title')} />
          {localGrid.type === LocalGridType.Electricity ? null : (
            <Tab
              id="borehole-field-tab"
              label={
                <LabelWithAlertBadge
                  label={t('boreholeField.title')}
                  showBadge={
                    !isConfirmationValid(localGrid, currentBoreholeData)
                  }
                />
              }
            />
          )}
        </Tabs>
      </Container>

      <Divider />

      {tab === 0 && <LocalGridBasicInfoTab localGrid={localGrid} />}
      {tab === 1 && <LocalGridEnergySystemTab localGrid={localGrid} />}
      {tab === 2 && <LocalGridReportTab localGrid={localGrid} />}
      {tab === 3 && <LocalGridBoreholeFieldTab localGrid={localGrid} />}
    </Box>
  )
}
